import { useNamespaceCopy } from '@/i18n/ns';
import pageCatalog from '@/i18n/ns/connections-page';
import { isConnecting, type ScopedConnectionItem } from './connection-api';
import {
  CatalogueCard,
  connectedTileAction,
  tileStateFor,
  type CatalogueConnection,
  type CatalogueTileState,
} from './catalogue-card';
import type { CustomerAppEntry } from './pipedream-catalogue-api';

/**
 * Customer-owned apps of one kind (Meta's reporting and ads management) as one tile: what Poneva may
 * do is the setup's first choice, not two near-identical entries to pick between.
 */
export function CustomerAppCard({
  entries,
  busy,
  connection,
  onOpen,
}: {
  entries: CustomerAppEntry[];
  busy: string | null;
  /** The connected account behind any of these entries, when there is one. */
  connection: CatalogueConnection | null;
  onOpen: (items: ScopedConnectionItem[]) => void;
}) {
  const copy = useNamespaceCopy(pageCatalog);
  const items = entries.map((entry) => entry.item);
  // The broadest entry names the tile (Meta Ads, not Meta Ads reporting).
  const name =
    entries.find((entry) => entry.item.appSetup?.useCase === 'ads-mcp')?.name ??
    entries[0]?.name ??
    '';
  const connecting = items.some((item) => isConnecting(busy, item));
  const canConnect = items.some((item) => item.actions.connect.available);
  const state: CatalogueTileState = connecting
    ? 'busy'
    : connection
      ? tileStateFor(connection.state)
      : canConnect
        ? 'connect'
        : 'unavailable';
  const action =
    state === 'busy'
      ? { label: copy.tileConnecting, onClick: () => undefined }
      : connection
        ? connectedTileAction(state, connection, copy, Boolean(busy))
        : {
            label: canConnect ? copy.tileConnect : copy.tileUnavailable,
            disabled: !canConnect || Boolean(busy),
            onClick: () => onOpen(items),
          };
  return (
    <CatalogueCard
      source="customer-app"
      logoId={items[0]?.id}
      name={name}
      subtitle={
        connection ? (connection.identity ?? copy.tileConnected) : entries[0]?.categories?.[0]
      }
      description={entries.map((entry) => entry.description).join(' ')}
      state={state}
      action={action}
    />
  );
}
